// web/src/components/StageRail.tsx — pipeline stepper. Horizontal scroller on phone, wraps at md+.
// complete = run-green; running = info + pulse (reduced-motion safe); pending = surface; failed = danger.
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { Icon } from './Icon';
import type { RunStatus, Stage, StageName } from '../lib/types';

const ORDER: StageName[] = ['intake', 'analyst', 'planner', 'coder', 'qa', 'ci', 'deploy'];
const dot: Record<Stage['status'], string> = {
  pending: 'bg-surface2 text-muted',
  running: 'bg-info text-runink',
  complete: 'bg-success text-success-ink',
  failed: 'bg-danger text-runink',
};

export function StageRail({ stages }: { stages: Stage[] }) {
  const reduce = useReducedMotion();
  const byName = new Map(stages.map((s) => [s.name, s]));
  return (
    <ol className="flex gap-2 overflow-x-auto pb-2 md:flex-wrap md:overflow-visible">
      <AnimatePresence initial={false}>
        {ORDER.map((name) => {
          const s = byName.get(name) ?? { name, status: 'pending' as const };
          return (
            <motion.li key={name} layout={!reduce}
              initial={reduce ? false : { opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
              className="shrink-0">
              <div className={`flex min-h-11 items-center gap-2 rounded-md px-3 ${dot[s.status]}`}>
                <span className="font-head text-xs">{name}</span>
                {s.status === 'running' && <span className="size-2 animate-pulse rounded-full bg-runink" />}
              </div>
            </motion.li>
          );
        })}
      </AnimatePresence>
    </ol>
  );
}

// ── The customer's update rail (CHALICE_TASKS_SPEC §4a + 22_PREVIEW_AND_PUBLISH.md §3) ─────────
// Lifted out of RequestDetail so the rail and the deprecated build stepper above share one file,
// one set of style tokens, and one phone behaviour (scroller under md, wraps at md+).
//
// SIX steps now, not five: the QA gate (doc 20) is a real wait the customer sits through, and an
// unexplained pause inside "Building" reads as a stall. "Checking" is the honest name for it and
// it says nothing about what is being checked (doc 20 §7 item 9).
const REQUEST_RAIL = ['Received', 'Questions', 'Building', 'Checking', 'Preview', 'Live'] as const;

/** Which step each status lights up. `needs_attention` holds at Checking — we really are still
 *  looking at it — and `awaiting_credits` holds at Received, because no work has started. */
const ACTIVE_STEP: Partial<Record<RunStatus, number>> = {
  queued: 0,
  waiting: 0, // portal spec 19: another change on the site goes first
  publish_waiting: 5, // approved; goes live right after the change ahead of it
  awaiting_credits: 0,
  clarifying: 1,
  working: 2,
  verifying: 3,
  needs_attention: 3,
  preview_ready: 4,
  merging: 5,
  live: 5,
};

/** A hold is not a failure: red would tell the customer something broke, and for both of these
 *  states nothing has. Warn + its own copy elsewhere on the page. */
const HELD: RunStatus[] = ['needs_attention', 'awaiting_credits'];
/** Steps that should pulse — work is actually happening right now. */
const PULSING: RunStatus[] = ['queued', 'working', 'verifying', 'merging'];

type RailState = 'done' | 'active' | 'pending' | 'failed' | 'held';
const RAIL_STYLE: Record<RailState, string> = {
  done: 'bg-success text-success-ink',
  active: 'bg-info text-runink',
  pending: 'bg-surface2 text-muted',
  failed: 'bg-danger text-runink',
  // Outlined, not filled: `--run-ink` is white and `--warn` is amber, which fails contrast as a
  // solid pill in both themes. `ring-inset` keeps every pill the same height (a border would not).
  held: 'bg-surface2 text-warn ring-1 ring-inset ring-warn',
};

export function RequestRail({ status, hasPreview }: { status: RunStatus; hasPreview: boolean }) {
  const isFailed = status === 'failed';
  const isHeld = HELD.includes(status);
  // A failed build stops at Building unless a preview already exists (then the snag is at go-live).
  const active = isFailed ? (hasPreview ? 5 : 2) : (ACTIVE_STEP[status] ?? 0);
  const doneAll = status === 'live';
  return (
    <ol className="flex gap-2 overflow-x-auto pb-1 md:flex-wrap md:overflow-visible">
      {REQUEST_RAIL.map((label, i) => {
        const state: RailState = doneAll || i < active
          ? 'done'
          : i === active
            ? (isFailed ? 'failed' : isHeld ? 'held' : 'active')
            : 'pending';
        return (
          <li key={label} className="shrink-0">
            <div className={`flex min-h-11 items-center gap-2 rounded-md px-3 ${RAIL_STYLE[state]}`}>
              {state === 'done' && <Icon name="check" size={13} />}
              <span className="font-head text-xs">
                {label === 'Live' && status === 'merging' ? 'Publishing' : label}
              </span>
              {state === 'active' && PULSING.includes(status) && (
                <span className="size-2 animate-pulse rounded-full bg-runink" />
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
